import { Row, Switch, Tooltip } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';

import useAPIQuery from '../../../api/useAPIQuery';
import careerjetLogo from '../../../assets/careerjet.png';
import googleForJobsLogo from '../../../assets/gfj.png';
import indeedLogo from '../../../assets/indeed.png';
import joobleLogo from '../../../assets/jooble.png';
import linkedinLogo from '../../../assets/linkedin.png';
import netvagasLogo from '../../../assets/netvagas.png';
import Form from '../../../components/Form';
import JobBoardCard from '../../../components/JobBoardCard';
import useTypedParams from '../../../hooks/useTypedParams';
import { JobPostingId } from '../../../types';
import { handleJobPostingDescriptionLengthValidator } from '../../../validators/jobPostingDescriptionValidator';
import { PublicationFormValues } from './PublicationForm';

interface JobBoardsCardsProps {
  loading?: boolean;
  disabled?: boolean;
}

interface GoogleForJobsTooltipTitleProps {
  hasCompanyConfig: boolean;
  hasVacancyJobLocation: boolean;
  unlistedExternalPublication: boolean;
}

const googleForJobsTooltipTitle = ({
  hasCompanyConfig,
  hasVacancyJobLocation,
  unlistedExternalPublication,
}: GoogleForJobsTooltipTitleProps) => {
  if (unlistedExternalPublication) {
    return 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.';
  }

  if (!hasCompanyConfig) {
    return 'Algumas informações gerais da empresa estão pendentes de configuração na plataforma. Contate o nosso suporte.';
  }

  if (!hasVacancyJobLocation) {
    return 'Verifique se os seguintes campos necessários para a vaga ser publicada no Google for Jobs estão preenchidos: Estado e Cidade.';
  }

  return null;
};

interface NetvagasTooltipTitleProps {
  hasNetvagasConfig: boolean;
  hasVacancyJobLocation: boolean;
  unlistedExternalPublication: boolean;
}

const netvagasTooltipTitle = ({
  hasNetvagasConfig,
  hasVacancyJobLocation,
  unlistedExternalPublication,
}: NetvagasTooltipTitleProps) => {
  if (unlistedExternalPublication) {
    return 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.';
  }

  if (!hasNetvagasConfig) {
    return 'Algumas informações gerais da empresa estão pendentes de configuração na plataforma. Contate o nosso suporte.';
  }

  if (!hasVacancyJobLocation) {
    return 'Verifique se os seguintes campos necessários para a vaga ser publicada no Netvagas estão preenchidos: Estado e Cidade.';
  }

  return null;
};

interface IndeedTooltipTitleProps {
  hasIndeedConfig: boolean;
  unlistedExternalPublication: boolean;
}

const indeedTooltipTitle = ({
  hasIndeedConfig,
  unlistedExternalPublication,
}: IndeedTooltipTitleProps) => {
  if (unlistedExternalPublication) {
    return 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.';
  }

  if (!hasIndeedConfig) {
    return 'Contate o CS responsável para ativar a integração.';
  }

  return null;
};

const JobBoardsCards = ({ loading, disabled }: JobBoardsCardsProps) => {
  const form = Form.useFormInstance<PublicationFormValues>();

  const config = useAPIQuery('/configs/tenant-configurations/');
  const { id } = useTypedParams({ id: JobPostingId });

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const jobPostingLocation =
    !!jobPosting.data?.country && !!jobPosting.data?.state && !!jobPosting.data?.city;

  const companyConfig =
    !!config.data?.businessName &&
    !!config.data?.companySiteUrl &&
    !!config.data?.companyLogoPreviewUrl;

  const googleForJobsExternalPublicationValue = Form.useWatch(
    'googleForJobsExternalPublication',
    form
  );
  const externalPublicationValue = Form.useWatch('externalPublication', form);
  const unlistedExternalPublicationValue = Form.useWatch('unlistedExternalPublication', form);
  const netvagasExternalPublicationValue = Form.useWatch('netvagasExternalPublication', form);
  const linkedinExternalPublicationValue = Form.useWatch('linkedinExternalPublication', form);
  const careerjetExternalPublicationValue = Form.useWatch('careerjetExternalPublication', form);
  const joobleExternalPublicationValue = Form.useWatch('joobleExternalPublication', form);

  const netvagasConfig = !!config.data?.businessName;

  return (
    <Row>
      <JobBoardCard
        logoSrc={googleForJobsLogo}
        logoAlt="Logo do Google for Jobs"
        tooltipTitle="Aguarde até 24h para a vaga aparecer no Google for jobs"
      >
        <Tooltip
          title={googleForJobsTooltipTitle({
            hasCompanyConfig: !!companyConfig,
            hasVacancyJobLocation: jobPostingLocation,
            unlistedExternalPublication: unlistedExternalPublicationValue,
          })}
        >
          {companyConfig ? (
            <>
              <Form.Item name="googleForJobsExternalPublication" noStyle valuePropName="checked">
                <Switch
                  defaultChecked={googleForJobsExternalPublicationValue}
                  loading={loading}
                  disabled={unlistedExternalPublicationValue || disabled}
                  onClick={async (checked) => {
                    form.setFieldsValue({
                      externalPublication: true,
                      googleForJobsExternalPublication: checked,
                    });
                    try {
                      if (checked) {
                        await form.validateFields([
                          'externalPublicationPeriod',
                          'description',
                          'country',
                          'state',
                          'city',
                        ]);
                      }
                      await handleJobPostingDescriptionLengthValidator(
                        undefined,
                        jobPosting.data?.description
                      );
                      form.submit();
                    } catch (err) {
                      form.setFieldsValue({
                        externalPublication: jobPosting.data?.external_publication,
                        googleForJobsExternalPublication: false,
                      });
                      if (err && typeof err === 'object' && 'errorFields' in err) {
                        form.setFields(err.errorFields as FieldData[]);
                      }
                    }
                  }}
                />
              </Form.Item>
            </>
          ) : (
            <Switch disabled checked={false} />
          )}
        </Tooltip>
      </JobBoardCard>
      {config.data?.enablePublicationNetvagasJobboard && (
        <JobBoardCard
          logoSrc={netvagasLogo}
          logoAlt="Logo do Netvagas"
          tooltipTitle="Aguarde até 24h para a vaga aparecer no Netvagas"
        >
          <Tooltip
            title={netvagasTooltipTitle({
              unlistedExternalPublication: unlistedExternalPublicationValue,
              hasNetvagasConfig: !!netvagasConfig,
              hasVacancyJobLocation: jobPostingLocation,
            })}
          >
            {netvagasConfig ? (
              <>
                <Form.Item name="netvagasExternalPublication" noStyle valuePropName="checked">
                  <Switch
                    defaultChecked={netvagasExternalPublicationValue}
                    loading={loading}
                    disabled={unlistedExternalPublicationValue || disabled}
                    onClick={async (checked) => {
                      form.setFieldsValue({
                        externalPublication: true,
                        netvagasExternalPublication: checked,
                      });
                      try {
                        if (checked) {
                          await form.validateFields([
                            'externalPublicationPeriod',
                            'description',
                            'country',
                            'state',
                            'city',
                          ]);
                        }
                        await handleJobPostingDescriptionLengthValidator(
                          undefined,
                          jobPosting.data?.description
                        );
                        form.submit();
                      } catch (err) {
                        form.setFieldsValue({
                          externalPublication: jobPosting.data?.external_publication,
                          netvagasExternalPublication: false,
                        });
                        if (err && typeof err === 'object' && 'errorFields' in err) {
                          form.setFields(err.errorFields as FieldData[]);
                        }
                      }
                    }}
                  />
                </Form.Item>
              </>
            ) : (
              <Switch disabled checked={false} />
            )}
          </Tooltip>
        </JobBoardCard>
      )}
      {config.data?.enableLinkedinIntegration && (
        <JobBoardCard
          logoSrc={linkedinLogo}
          logoAlt="Logo do LinkedIn"
          tooltipTitle="Aguarde até 48h para a vaga aparecer na página da sua empresa no LinkedIn."
        >
          <Tooltip
            title={
              unlistedExternalPublicationValue
                ? 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.'
                : null
            }
          >
            <>
              <Form.Item name="linkedinExternalPublication" valuePropName="checked" noStyle>
                <Switch
                  defaultChecked={linkedinExternalPublicationValue}
                  loading={loading}
                  disabled={unlistedExternalPublicationValue || disabled}
                  onClick={async (checked) => {
                    form.setFieldsValue({
                      externalPublication: true,
                      linkedinExternalPublication: checked,
                    });
                    try {
                      if (checked) {
                        await form.validateFields([
                          'externalPublicationPeriod',
                          'description',
                          'country',
                          'state',
                          'city',
                        ]);
                      }
                      await handleJobPostingDescriptionLengthValidator(
                        undefined,
                        jobPosting.data?.description
                      );
                      form.submit();
                    } catch (err) {
                      form.setFieldsValue({
                        externalPublication: jobPosting.data?.external_publication,
                        linkedinExternalPublication: false,
                      });
                      if (err && typeof err === 'object' && 'errorFields' in err) {
                        form.setFields(err.errorFields as FieldData[]);
                      }
                    }
                  }}
                />
              </Form.Item>
            </>
          </Tooltip>
        </JobBoardCard>
      )}
      {config.data?.enableCareerjetIntegration && (
        <JobBoardCard
          logoSrc={careerjetLogo}
          logoAlt="Logo do Careerjet"
          tooltipTitle="Aguarde até 48h para a vaga aparecer no CareerJet."
        >
          <Tooltip
            title={
              unlistedExternalPublicationValue
                ? 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.'
                : null
            }
          >
            <>
              <Form.Item name="careerjetExternalPublication" valuePropName="checked" noStyle>
                <Switch
                  defaultChecked={careerjetExternalPublicationValue}
                  loading={loading}
                  disabled={unlistedExternalPublicationValue || disabled}
                  onClick={async (checked) => {
                    form.setFieldsValue({
                      externalPublication: true,
                      careerjetExternalPublication: checked,
                    });
                    try {
                      if (checked) {
                        await form.validateFields([
                          'externalPublicationPeriod',
                          'description',
                          'country',
                          'state',
                          'city',
                        ]);
                      }
                      await handleJobPostingDescriptionLengthValidator(
                        undefined,
                        jobPosting.data?.description
                      );
                      form.submit();
                    } catch (err) {
                      form.setFieldsValue({
                        externalPublication: jobPosting.data?.external_publication,
                        careerjetExternalPublication: false,
                      });
                      if (err && typeof err === 'object' && 'errorFields' in err) {
                        form.setFields(err.errorFields as FieldData[]);
                      }
                    }
                  }}
                />
              </Form.Item>
            </>
          </Tooltip>
        </JobBoardCard>
      )}
      {config.data?.enableJoobleIntegration && (
        <JobBoardCard
          logoSrc={joobleLogo}
          logoAlt="Logo do Jooble"
          tooltipTitle="Aguarde até 48h para a vaga aparecer no Jooble."
        >
          <Tooltip
            title={
              unlistedExternalPublicationValue
                ? 'A vaga não pode ser publicada nos canais de divulgação, pois está configurada como não listada.'
                : null
            }
          >
            <>
              <Form.Item name="joobleExternalPublication" valuePropName="checked" noStyle>
                <Switch
                  defaultChecked={joobleExternalPublicationValue}
                  loading={loading}
                  disabled={unlistedExternalPublicationValue || disabled}
                  onClick={async (checked) => {
                    form.setFieldsValue({
                      externalPublication: true,
                      joobleExternalPublication: checked,
                    });
                    try {
                      if (checked) {
                        await form.validateFields([
                          'externalPublicationPeriod',
                          'description',
                          'country',
                          'state',
                          'city',
                        ]);
                      }
                      await handleJobPostingDescriptionLengthValidator(
                        undefined,
                        jobPosting.data?.description
                      );
                      form.submit();
                    } catch (err) {
                      form.setFieldsValue({
                        externalPublication: jobPosting.data?.external_publication,
                        joobleExternalPublication: false,
                      });
                      if (err && typeof err === 'object' && 'errorFields' in err) {
                        form.setFields(err.errorFields as FieldData[]);
                      }
                    }
                  }}
                />
              </Form.Item>
            </>
          </Tooltip>
        </JobBoardCard>
      )}
      <JobBoardCard
        logoSrc={indeedLogo}
        logoAlt="Logo do Indeed"
        tooltipTitle="Todas as vagas publicadas na página de carreiras são automaticamente publicadas no Indeed."
      >
        <Tooltip
          title={indeedTooltipTitle({
            hasIndeedConfig: Boolean(config.data?.enableIndeedIntegration),
            unlistedExternalPublication: unlistedExternalPublicationValue,
          })}
        >
          {config.data?.enableIndeedIntegration ? (
            <Switch disabled checked={externalPublicationValue} />
          ) : (
            <Switch disabled checked={false} />
          )}
        </Tooltip>
      </JobBoardCard>
    </Row>
  );
};

export default JobBoardsCards;
